import { trackEvent } from '@/lib/trackEvent';
import {
  createReportSessionMetrics,
  type ReportSessionMetrics,
} from '@/lib/reportSessionMetrics';

export const trackReportClosed = (metrics: ReportSessionMetrics): boolean => {
  const snapshot = metrics.getSnapshot();
  return trackEvent('report_closed', snapshot, { transport: 'sendBeacon' });
};

export const startReportClosedTracking = (
  root: ParentNode = document
): (() => void) => {
  const metrics = createReportSessionMetrics();
  let closed = false;
  let sectionObserver: IntersectionObserver | null = null;

  const flush = () => {
    if (closed) return;
    closed = true;
    metrics.stop();
    if (sectionObserver) {
      sectionObserver.disconnect();
      sectionObserver = null;
    }
    trackReportClosed(metrics);
  };

  metrics.start();

  if (typeof IntersectionObserver !== 'undefined') {
    sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const sectionId =
            (entry.target as HTMLElement).dataset.reportSection ||
            (entry.target as HTMLElement).id;
          if (sectionId) {
            metrics.markSectionViewed(sectionId);
          }
        });
      },
      { threshold: 0.35 }
    );

    root.querySelectorAll('[data-report-section]').forEach((element) => {
      sectionObserver?.observe(element);
    });
  }

  window.addEventListener('pagehide', flush);
  window.addEventListener('beforeunload', flush);

  return () => {
    window.removeEventListener('pagehide', flush);
    window.removeEventListener('beforeunload', flush);
    flush();
  };
};
